import { DialogContent, DialogTitle, Grid } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { autocompleteClientName, getFullTextSearchString } from 'wisestep-api';
import { WsButton, WsIconCross, WsTypography, WsTypoVariants } from 'wisestep-base';

import ClientDetailsForm from '../features/clientListingPage/components/ClientDetailsForm';
import { defaultClientDTO } from '../features/clientListingPage/helper/ClientListingHelper';
import { useDialog } from '../hooks/useDialog';

import { WsAsyncAutocomplete } from './WsAsyncAutocomplete';

interface ClientAutocompleteProps {
  name: string;
  multiple?: boolean;
  placeHolder?: string;
  creatable?: boolean;
  required?: boolean;
}

export const ClientAutocomplete = ({
  name,
  multiple = false,
  placeHolder = 'Filter by Client Names',
  creatable = false,
  required = false,
}: ClientAutocompleteProps) => {
  const methods = useFormContext();
  const [openDialog, closeDialog] = useDialog();

  const getOptions = async (clientSearch: string) => {
    const result = await autocompleteClientName(
      encodeURIComponent(getFullTextSearchString(clientSearch))
    );
    if (result?.data) {
      const selected = methods.getValues(name) ?? [];
      let suggestions = result.data
        .map((client) => ({ ...client, id: client.value }))
        .filter((client) => !selected?.some((selectedClient) => selectedClient.id === client.id));
      if (creatable && suggestions.length === 0) {
        suggestions = [
          { inputValue: clientSearch, name: `${clientSearch}  (Not Found - Add client)` },
        ];
      }
      return suggestions;
    }
    return [];
  };

  const onCreate = (clientName, setValue) => {
    openDialog({
      onClose: () => {
        methods.setValue(name, null);
      },
      children: (
        <>
          <DialogTitle>
            <Grid item container justifyContent="space-between">
              <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                Create Client
              </WsTypography>
              <WsButton startIcon={<WsIconCross />} onClick={() => closeDialog()} variant="text">
                Close
              </WsButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <Grid p={2}>
              <ClientDetailsForm
                source="JobPostingForm"
                defaultValues={{ ...defaultClientDTO, name: clientName?.trim() }}
                getClient={(client) => {
                  closeDialog();
                  setValue(client);
                }}
              />
            </Grid>
          </DialogContent>
        </>
      ),
    });
  };

  return (
    <WsAsyncAutocomplete
      name={name}
      getOptions={getOptions}
      multiple={multiple}
      placeholder={placeHolder}
      labelField="name"
      creatable={creatable}
      onCreate={onCreate}
      required={required}
    />
  );
};
